import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import JSZip from "jszip";
import { useAuth } from "@/context/AuthContext";
import { restoreText, restoredFilename, isAcceptedFile, ACCEPTED_EXT } from "@/lib/scrubber";
import { readKeyFile, isEncryptedKeyFile } from "@/lib/keyfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Upload, KeyRound, X, Download, Package, Puzzle, FileCheck2, AlertTriangle, RotateCcw } from "lucide-react";

export default function Restore() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]); // [{name,text}]
  const [keyName, setKeyName] = useState("");
  const [keyText, setKeyText] = useState("");
  const [encrypted, setEncrypted] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [results, setResults] = useState([]); // [{name, output, report}]
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const keyRef = useRef(null);

  const loadFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => isAcceptedFile(f.name));
    if (!arr.length) { toast.error(`Accepted: ${ACCEPTED_EXT.join(", ")}`); return; }
    Promise.all(arr.map((file) => new Promise((res) => {
      const r = new FileReader();
      r.onload = (e) => res({ name: file.name, text: String(e.target.result || "") });
      r.readAsText(file);
    }))).then((loaded) => {
      setFiles((prev) => {
        const merged = [...prev];
        loaded.forEach((nf) => { const i = merged.findIndex((m) => m.name === nf.name); if (i >= 0) merged[i] = nf; else merged.push(nf); });
        return merged;
      });
      setResults([]);
    });
  };

  const loadKey = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = (e) => {
      const text = String(e.target.result || "");
      setKeyText(text); setKeyName(file.name); setEncrypted(isEncryptedKeyFile(text)); setResults([]);
    };
    r.readAsText(file);
  };

  const runRestore = async () => {
    if (!files.length) return toast.error("Add the contractor's tested file(s) first.");
    if (!keyText) return toast.error("Add your restore key file.");
    setBusy(true);
    try {
      const mapping = await readKeyFile(keyText, passphrase.trim() || undefined);
      const out = files.map((f) => ({ name: f.name, ...restoreText(f.text, mapping) }));
      setResults(out); setActiveIdx(0);
      toast.success("Secrets restored locally");
    } catch (err) {
      toast.error(err.message || "Could not read key file");
    } finally {
      setBusy(false);
    }
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const active = results[activeIdx];
  const downloadActive = () => { if (active) { triggerDownload(new Blob([active.output], { type: "text/plain;charset=utf-8" }), restoredFilename(active.name)); toast.success("Restored file downloaded"); } };
  const downloadZip = async () => {
    const zip = new JSZip();
    results.forEach((r) => zip.file(restoredFilename(r.name), r.output));
    triggerDownload(await zip.generateAsync({ type: "blob" }), "restored_configs.zip");
    toast.success("Downloaded restored files as .zip");
  };

  const missing = active ? active.report.filter((r) => r.found === 0) : [];
  const restored = active ? active.report.filter((r) => r.found > 0) : [];

  return (
    <div className="min-h-screen w-full bg-background text-foreground flex flex-col">
      <header className="h-12 border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
        <Link to="/" data-testid="restore-back-link">
          <Button variant="ghost" size="sm" className="rounded-none h-8 text-xs text-muted-foreground"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Scrubber</Button>
        </Link>
        <span className="text-sm font-semibold tracking-tight flex items-center gap-2 ml-2"><Puzzle className="h-4 w-4 text-primary" /> Restore Secrets</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-2 py-1 ml-auto">100% Local</span>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left: inputs */}
        <aside className="w-full lg:w-96 flex-shrink-0 border-r border-border bg-surface p-4 space-y-5 overflow-auto">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">1 · Contractor's tested file(s)</p>
            <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); loadFiles(e.dataTransfer.files); }}
              className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-primary/60 cursor-pointer py-6 transition-colors" data-testid="restore-file-dropzone">
              <input ref={fileRef} type="file" data-testid="restore-file-input" multiple accept={ACCEPTED_EXT.join(",")} className="hidden" onChange={(e) => loadFiles(e.target.files)} />
              <Upload className="h-6 w-6 text-primary mb-2" strokeWidth={1.5} />
              <span className="font-mono text-xs text-muted-foreground">drop edited sanitized file(s)</span>
            </label>
            {files.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="restore-file-list">
                {files.map((f, i) => (
                  <div key={f.name} className="flex items-center gap-2 text-xs font-mono border border-border bg-background px-2 py-1">
                    <FileCheck2 className="h-3.5 w-3.5 text-green-400" />
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="ml-auto text-muted-foreground hover:text-red-400"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">2 · Your restore key</p>
            <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); loadKey(e.dataTransfer.files?.[0]); }}
              className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-primary/60 cursor-pointer py-6 transition-colors" data-testid="restore-key-dropzone">
              <input ref={keyRef} type="file" data-testid="restore-key-input" accept=".sxmap,.json,application/json" className="hidden" onChange={(e) => loadKey(e.target.files?.[0])} />
              <KeyRound className="h-6 w-6 text-primary mb-2" strokeWidth={1.5} />
              <span className="font-mono text-xs text-muted-foreground">{keyName || "drop restore-key.sxmap"}</span>
            </label>
            {encrypted && (
              <div className="mt-2">
                <Input data-testid="restore-passphrase-input" type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} placeholder="key passphrase" className="font-mono text-xs rounded-none bg-background border-border h-9 focus-visible:ring-primary" />
                <p className="text-[10px] text-muted-foreground mt-1">This key file is encrypted — enter its passphrase.</p>
              </div>
            )}
          </div>

          <Button data-testid="run-restore-button" onClick={runRestore} disabled={busy} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium h-10">
            <RotateCcw className="h-4 w-4 mr-2" /> Put the puzzle back
          </Button>

          {active && (
            <div className="border border-border bg-background" data-testid="restore-report">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <span className="text-xs text-muted-foreground">Restored in file</span>
                <span data-testid="restore-count" className="text-sm font-mono font-semibold text-green-400">{restored.reduce((s, r) => s + r.found, 0)}</span>
              </div>
              {missing.length > 0 ? (
                <div className="px-3 py-2 space-y-1" data-testid="restore-missing">
                  <p className="flex items-center gap-1.5 text-[11px] text-yellow-400"><AlertTriangle className="h-3.5 w-3.5" /> {missing.length} token(s) not found (deleted/altered by editor):</p>
                  {missing.slice(0, 8).map((m) => <p key={m.token} className="text-[11px] font-mono text-muted-foreground truncate">{m.token}</p>)}
                </div>
              ) : (
                <p className="px-3 py-2 text-[11px] text-green-400">All tokens matched and restored.</p>
              )}
            </div>
          )}
        </aside>

        {/* Right: preview */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {results.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-8 text-center">
              <Puzzle className="h-10 w-10 text-primary/60" strokeWidth={1.4} />
              <p className="font-mono text-sm">Add the tested file(s) + your key, then restore.</p>
              <p className="font-mono text-xs text-muted-foreground/70">Renaming config sections is fine — only the placeholder tokens matter.</p>
            </div>
          ) : (
            <>
              <div className="h-12 border-b border-border flex items-center justify-between px-4 gap-3 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
                  {results.map((r, i) => (
                    <button key={r.name} onClick={() => setActiveIdx(i)} data-testid={`restore-tab-${i}`}
                      className={`text-xs font-mono px-2 py-1 border flex-shrink-0 ${i === activeIdx ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>
                      {restoredFilename(r.name)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {results.length > 1 && <Button data-testid="restore-download-zip" onClick={downloadZip} size="sm" className="rounded-none h-8 text-xs bg-primary text-primary-foreground hover:bg-accent-hover font-medium"><Package className="h-3.5 w-3.5 mr-1.5" /> All (.zip)</Button>}
                  <Button data-testid="restore-download-button" onClick={downloadActive} size="sm" variant={results.length > 1 ? "outline" : "default"} className={results.length > 1 ? "rounded-none h-8 text-xs border-border" : "rounded-none h-8 text-xs bg-primary text-primary-foreground hover:bg-accent-hover font-medium"}>
                    <Download className="h-3.5 w-3.5 mr-1.5" /> {results.length > 1 ? "This File" : "Download Restored"}
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-auto bg-background" data-testid="restore-preview">
                <div className="font-mono text-xs leading-[1.7]">
                  {active.output.split("\n").map((line, i) => (
                    <div key={i} className="flex hover:bg-white/[0.03]">
                      <span className="select-none text-right w-12 flex-shrink-0 pr-3 text-muted-foreground/50 border-r border-border">{i + 1}</span>
                      <code className="px-3 whitespace-pre-wrap break-all flex-1">{line || "\u00A0"}</code>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
